/**
 * @module hooks/dva/useQueryColumnLists
 * @description QB / 数据源列表专用订阅（需跟随「原地改名 + put 新数组」刷新的 UI）
 *
 * ## 调用方（修改 equality 策略前请评估对这些模块的影响）
 *
 * - `settingGroups/ConditionalFormat` — 条件格式摘要指标名
 * - `settingGroups/TableFieldSetting/FieldColumnSorting` — 字段列排序拖拽列表
 * - `settingGroups/TableFieldSetting/UrlSetting` — 维度 URL prefix
 * - `linkage/qbFields/useQBCategoryPartials` — QB 联动 diff 的 deps
 * - `XAxisSortBy/useXAxisSortDraft` — 字段排序下拉（dataTables / customColumns）
 *
 * ## model 写入方式（updateTableField / updateCustomColumn 同类路径）
 *
 * 1. `Object.assign(item, updateColumn)` — 原地改 `column_display` / `name_cn` 等
 * 2. `put({ columnMetrics: [...columnMetrics] })` — **换新数组引用**，元素引用不变
 *    （customColumns / dataTables 同理）
 *
 * ## 为何不能用默认 shallowEqual？
 *
 * shallowEqual 比较数组**元素引用**。步骤 2 之后元素引用未变 → 判定「相等」→ 订阅组件不重渲染。
 *
 * ## 为何不能用 fieldListSignature 做 equalityFn？
 *
 * useSelector 回调拿到的 prev/next 往往是**同一批对象**（步骤 1 已 mutate）。
 * 对 prev、next 各算签名时读到的都是**改名后**的展示名 → 签名相同 → 仍不重渲染。
 * QB 联动 pipeline 的 diff 能工作，是因为 `freezeQBSnapshot` 在 mutate **之前**冻结了 prev。
 *
 * ## 正确做法：数组引用相等（`queryColumnListRefEqual`）
 *
 * model 每次 put 都会 `[...list]`，新数组 `!==` 旧数组 → 触发重渲染；渲染时 item 已是新展示名。
 * 仅当 store 里列表引用未变时才跳过更新（与 model 契约一致）。
 *
 * @see linkage/qbFields/qbLinkage/fieldSnapshot — 签名用于 pipeline diff，不用于本 hook
 * @see __tests__/useQueryColumnLists.test.ts — updateTableField 场景回归
 */

import type { IColumnInfo } from '@dbl/core/dataSource';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import type { CustomColumn } from '@dbl/pages/Multidimensional/Analysis/interface';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import { useQuerySelector } from './useQuerySelectorDva';

const EMPTY_DATA_TABLES: readonly IGetTableInfoDto[] = [];
const EMPTY_CUSTOM_COLUMNS: readonly CustomColumn[] = [];

/**
 * useSelector equalityFn：返回 true 表示 prev/next「相等」、跳过重渲染。
 * 仅比较数组引用，对齐 model `put([...list])` 的更新方式。
 */
export function queryColumnListRefEqual(
  left: readonly unknown[],
  right: readonly unknown[],
): boolean {
  return left === right;
}

/**
 * 订阅 QB 维度列表（columnDimensions）。
 * 字段展示名/增删/排序变更后 model 会 put 新数组引用，本 hook 可触发 UI 更新。
 */
export function useQueryColumnDimensions(): IColumnInfo[] {
  return useQuerySelector(
    state => (state.columnDimensions ?? EMPTY_COLUMN_INFO_LIST) as IColumnInfo[],
    queryColumnListRefEqual,
  );
}

/**
 * 订阅 QB 指标列表（columnMetrics）。
 */
export function useQueryColumnMetrics(): IColumnInfo[] {
  return useQuerySelector(
    state => (state.columnMetrics ?? EMPTY_COLUMN_INFO_LIST) as IColumnInfo[],
    queryColumnListRefEqual,
  );
}

/**
 * 订阅 QB 明细模式原始列（columnRawColumns）。
 */
export function useQueryColumnRawColumns(): IColumnInfo[] {
  return useQuerySelector(
    state =>
      (state.columnRawColumns ?? EMPTY_COLUMN_INFO_LIST) as IColumnInfo[],
    queryColumnListRefEqual,
  );
}

/**
 * 订阅数据表列表（dataTables）。
 * 用于字段排序等：左侧改表字段展示名后需刷新下拉文案。
 */
export function useQueryDataTables(): IGetTableInfoDto[] {
  return useQuerySelector(
    state => (state.dataTables ?? EMPTY_DATA_TABLES) as IGetTableInfoDto[],
    queryColumnListRefEqual,
  );
}

/**
 * 订阅自定义字段列表（customColumns）。
 * 用于字段排序等：改 name_cn 后需刷新下拉选项与选中回填。
 */
export function useQueryCustomColumns(): CustomColumn[] {
  return useQuerySelector(
    state => (state.customColumns ?? EMPTY_CUSTOM_COLUMNS) as CustomColumn[],
    queryColumnListRefEqual,
  );
}
